// features/workstation-role-bar.js — PARTICIPANT ROLE BAR
// ═══════════════════════════════════════════════════════════
// Horizontal strip of participant cards. Replaces the simple
// "Conversation" header in the workstation chat panel. Shows each
// participant's avatar, name, role, permission marker, and live
// state (online / typing / building).
//
// For Solo workspaces:
//   [User card]  [Shift card]
//
// For multi-user (Phase 3+ Workspace modes):
//   [User1] [User2] [User3] [Shift]
//
// Visual states:
//   - online   → static colored underline (0.35 opacity)
//   - typing   → typing dots animated next to name
//   - speaking → underline pulses to full opacity (last speaker)
//   - building → gold underline pulses (Shift is doing canvas work)
// ═══════════════════════════════════════════════════════════

import { useState, useEffect } from 'react';

// Color palette — matches the workspace neutral system
const COLOR_HUMAN = '#7a9e82';   // sage
const COLOR_SHIFT = '#c8a96e';   // gold
const COLOR_PARTICIPANT_2 = '#9b8fc4'; // purple — second human in multi-user
const COLOR_PARTICIPANT_3 = '#4db8b8'; // teal — third human in multi-user

const HUMAN_COLORS = [COLOR_HUMAN, COLOR_PARTICIPANT_2, COLOR_PARTICIPANT_3];

function TypingDots({ color }) {
  return (
    <span style={{ display: 'inline-flex', gap: 2, marginLeft: 4 }}>
      {[0, 1, 2].map(i => (
        <span
          key={i}
          style={{
            display: 'inline-block',
            width: 3,
            height: 3,
            borderRadius: '50%',
            background: color,
            animation: `wsbar-blink 1.2s ${i * 0.2}s infinite`,
          }}
        />
      ))}
    </span>
  );
}

export default function WorkstationRoleBar({ participants, lastSpeakerId }) {
  if (!participants || participants.length === 0) {
    return null;
  }

  return (
    <>
      <style jsx global>{`
        @keyframes wsbar-blink {
          0%, 100% { opacity: 0.15; }
          50% { opacity: 1; }
        }
        @keyframes wsbar-pulse {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 1; }
        }
      `}</style>
      <div
        style={{
          display: 'flex',
          borderBottom: '1px solid #1e1e2a',
          background: '#07070a',
          flexShrink: 0,
        }}
      >
        {participants.map((p, i) => {
          const isLastSpeaker = p.id === lastSpeakerId;
          const isTyping = p.state === 'typing';
          const isBuilding = p.state === 'building';
          const isAnimated = isTyping || isBuilding || isLastSpeaker;

          return (
            <div
              key={p.id}
              style={{
                flex: 1,
                padding: '8px 10px',
                display: 'flex',
                alignItems: 'center',
                gap: 7,
                position: 'relative',
                borderRight: i < participants.length - 1 ? '1px solid #141414' : 'none',
                background: isBuilding ? 'rgba(200,169,110,0.03)' : 'transparent',
                minWidth: 0,
              }}
            >
              {/* Bottom border accent — colored, animated for active states */}
              <div
                style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: 2,
                  background: p.color,
                  opacity: isLastSpeaker || isTyping || isBuilding ? 1 : 0.25,
                  animation: isAnimated ? 'wsbar-pulse 1.8s infinite' : 'none',
                }}
              />

              {/* Avatar */}
              <div
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 9,
                  fontFamily: 'Georgia, serif',
                  flexShrink: 0,
                  background: `${p.color}22`,
                  color: p.color,
                  border: isBuilding ? `1px solid ${p.color}66` : `1px solid ${p.color}33`,
                }}
              >
                {p.initials}
              </div>

              {/* Name + role/state */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 11,
                    color: '#d4d4d4',
                    fontFamily: 'Georgia, serif',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {p.name}
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  {isTyping ? (
                    <>
                      <span
                        style={{
                          fontSize: 9,
                          color: p.color,
                          fontFamily: 'Georgia, serif',
                          fontStyle: 'italic',
                        }}
                      >
                        typing
                      </span>
                      <TypingDots color={p.color} />
                    </>
                  ) : isBuilding ? (
                    <span
                      style={{
                        fontSize: 9,
                        color: COLOR_SHIFT,
                        fontFamily: 'Georgia, serif',
                        fontStyle: 'italic',
                      }}
                    >
                      building
                    </span>
                  ) : (
                    <span
                      style={{
                        fontSize: 9,
                        color: p.color,
                        fontFamily: 'Georgia, serif',
                        opacity: 0.85,
                      }}
                    >
                      {p.role}
                    </span>
                  )}
                </div>
              </div>

              {/* Permission marker */}
              <span
                style={{
                  fontSize: 11,
                  color: p.isHost ? p.color : '#2a2a2a',
                  fontFamily: 'Georgia, serif',
                  flexShrink: 0,
                }}
                title={p.isHost ? 'Host' : 'Participant'}
              >
                {p.isHost ? '◈' : '○'}
              </span>
            </div>
          );
        })}
      </div>
    </>
  );
}

// ── Helper: build participants array for a Solo workspace ────
// Used by WorkstationChatPanel to derive the participants list
// from the workspace session + user record. The two flags drive
// the live state of each card:
//   - isShiftLoading → Shift card animates "typing"
//   - isUserTyping   → user card animates "typing" (input has chars)
export function buildSoloParticipants({ userId, userRecord, workspaceSession, isShiftLoading, isUserTyping }) {
  const firstName = userRecord?.first_name || '';
  const lastName = userRecord?.last_name || '';
  const username = userRecord?.username || 'You';

  const initials = (
    (firstName?.[0] || username?.[0] || 'U').toUpperCase() +
    (lastName?.[0] || '').toUpperCase()
  ).slice(0, 2);

  const role = workspaceSession?.workspace_metadata?.role || 'Solo';
  const isHost = workspaceSession?.created_by === userId;

  const aiName = userRecord?.ai_name || 'Shift';

  return [
    {
      id: userId,
      initials,
      name: firstName || username,
      role,
      isHost,
      color: HUMAN_COLORS[0],
      state: isUserTyping ? 'typing' : 'online',
    },
    {
      id: 'shift',
      initials: aiName[0]?.toUpperCase() || 'S',
      name: aiName,
      role: 'AI Assistant',
      isHost: true,
      color: COLOR_SHIFT,
      state: isShiftLoading ? 'typing' : 'online',
    },
  ];
}
